import { contractDocumentId } from '../st-folder-viewer/document-viewer-extensions/document-contract'
import {
  selViewerFitToWidthZoom,
  selViewerZoomLevel,
  type ViewerNavigation,
  type ViewerPresentation
} from '@st/document-viewer-kit'
import { titledPageGroups } from '~/st-doc-viewer/page-groups'
import {
  Update,
  child,
  defModule,
  type ChildHandle,
  type Module,
  type TaskMessage,
  type UpdateResult
} from '@st/redux'
import type { AuthStatusEvent } from '@features/auth/auth-client-adapter'
import { ANNOTATIONS_PLUGIN_ID } from '@st/document-viewer-kit/plugins/annotations'
import type { Contract, ContractField, FolderId, Signer, STDocument } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { SdkClient } from '~/effects'
import { observeAuth, observeRoute } from '~/features/module-runtime'
import {
  ST_DOC_VIEWER_ZOOM_LEVELS,
  type STDocViewerDocument,
  type STDocViewerRuntimeMessage,
  type STDocViewerRuntimeState
} from '~/st-doc-viewer/runtime-definition'
import {
  viewerFieldAnnotation,
  type ViewerFieldAnnotation
} from '~/st-doc-viewer/viewer-annotations'
import {
  type ActiveContractSigningState,
  type ContractKind,
  type ContractSigningState,
  type ContractSigningMessage,
  type ContractSigningWizardMessage,
  type SigningTarget,
  startOpeningSigningWizard,
  contractSigningWizardModule,
  contractKind,
  contractSigner,
  contractSignerEntries,
  displayedContractSignerId,
  activeSignatureField,
  signatureEditable,
  signerRoleTitle,
  fieldValues,
  signingFieldLabel
} from './contract-signing-wizard-module'

/**
 * Composes the PDF-blind signing journey with the PDF viewer, route/auth
 * lifetime, and the non-wizard publishing/signed surfaces.
 *
 * The journey owns signing and verification. After every shell message, one
 * projection pass updates the viewer. A new route cannot omit that projection.
 */

export type ContractViewerDocument = STDocViewerDocument & {
  name: string
  downloadUrl?: string
  pageGroups: STDocument['pageGroups']
}

// Preparation is an accepted write: leaving hides it but keeps its owner until
// draft creation and publication finish. Its result only opens the original view.
type ContractSurface =
  | { mode: 'closed' }
  | {
      mode: 'preparingEngagementLetter'
      folderId: FolderId
      signer: { type: 'first' } | { type: 'selected'; role: string }
      openWhenReady: boolean
    }
  | {
      mode: 'signing'
      document: ContractViewerDocument
      signing: ActiveContractSigningState
      paymentPromptOpen: boolean
    }
  | { mode: 'finishing'; signing: Extract<ContractSigningState, { status: 'finishing' }> }
  // Final submission was refused after the invoice became required or unpaid.
  | {
      /** Signing is blocked; reading the contract remains available. */
      mode: 'paymentRequired'
      /** Folder owning the invoice requirement and contract. */
      folderId: FolderId
      /** Contract whose submission was refused. */
      contractId: string
    }
  // Read-only viewing of a submitted contract.
  | {
      mode: 'signed'
      folderId: FolderId
      contractId: string
      document: ContractViewerDocument
    }

type ContractViewerPage = Pick<STDocViewerRuntimeState['pages'][number], 'pageNumber' | 'bounds'>

type ContractViewerState = {
  viewer: STDocViewerRuntimeState['viewer']
  pages: ReadonlyArray<ContractViewerPage>
}

/**
 * The surface is the authoritative lifecycle; signing exists only while its
 * dialog is active or an accepted write is finishing out of view.
 *
 * - the signing surface owns the wizard and its source PDF;
 * - the viewer document, annotations, and active-field navigation are derived
 *   from the surface and signing wizard rather than independently authored.
 */
type ContractSigningShellState<ViewerState extends ContractViewerState> = {
  surface: ContractSurface
  viewer: ViewerState
  // Delivery bookkeeping: remember which cursor has already caused a scroll,
  // so manual browsing is not undone by unrelated viewer messages. Each new
  // scroll gets a sequence number, even when it targets the same page.
  navigationSeq: number
  navigatedFieldId: string | undefined
  // The latest scroll request for the open document, as made: the viewer
  // places it once the page is laid out and owns whether it has landed.
  navigation: ViewerNavigation | undefined
}

const observeContractRoute = observeRoute({ folderId: P.string })
type ContractSigningShellMessage =
  | {
      type: 'signing'
      message: ContractSigningWizardMessage
      paymentRequired?: boolean
    }
  // Card intents. The card sends what it has; whether a contract opens
  // directly, is prepared first, or cannot open is decided here.
  | {
      type: 'prepareEngagementLetter'
      folderId: FolderId
      signer: { type: 'first' } | { type: 'selected'; role: string }
    }
  | {
      type: 'openContract'
      folderId: FolderId
      contract: Contract & { status: 'published' }
      document: STDocument
      target: SigningTarget
    }
  | { type: 'paymentRequiredClosed' }
  | {
      type: 'signedDocumentOpened'
      folderId: FolderId
      contract: Contract & { status: 'submitted' }
      document: STDocument
    }
  | { type: 'signedDocumentClosed' }
  | {
      type: 'engagementLetterPrepared'
      folderId: FolderId
      contract: Contract & { status: 'published' }
      document: STDocument
    }
  | { type: 'viewer'; message: STDocViewerRuntimeMessage; paymentRequired?: boolean }
  | { type: 'bookmarkSelected'; pageNumber: number }
  | { type: 'zoomClicked'; direction: 'in' | 'out' }
  | TaskMessage<typeof observeContractRoute>
  | { type: 'authStatusChanged'; event: AuthStatusEvent }

type ContractSigningShellUpdate<ViewerState extends ContractViewerState> = UpdateResult<
  ContractSigningShellState<ViewerState>,
  ContractSigningShellMessage
>

type ContractViewerChild<ViewerState extends ContractViewerState> = ChildHandle<
  ViewerState,
  STDocViewerRuntimeMessage,
  'viewer',
  undefined
>

type ContractSigningChild = ChildHandle<
  ContractSigningState,
  ContractSigningWizardMessage,
  'signing',
  Parameters<typeof startOpeningSigningWizard>[0]
>

function folderIdFromRoute(
  route: TaskMessage<typeof observeContractRoute>['route']
): FolderId | undefined {
  return route?.folderId as FolderId | undefined
}

const observeContractAuth = observeAuth(
  (event): ContractSigningShellMessage => ({ type: 'authStatusChanged', event })
)

export function makeContractSigningShellModule<ViewerState extends ContractViewerState>(
  viewerModule: Module<ViewerState, STDocViewerRuntimeMessage, undefined>
) {
  const signing = child(contractSigningWizardModule, 'signing')
  const viewer = child(viewerModule, 'viewer')

  return defModule<{ state: ContractSigningShellState<ViewerState>; message: ContractSigningShellMessage }>({
    layer: viewer.layer,
    init: () =>
      Update.mapState(viewer.init(undefined), (viewer) => ({
        surface: { mode: 'closed' },
        viewer,
        navigationSeq: 0,
        navigatedFieldId: undefined,
        navigation: undefined
      })),
    tasks: (state) => [
      ...match(state.surface)
        .with({ mode: P.union('signing', 'finishing') }, (surface) => signing.tasks(surface.signing))
        .otherwise(() => []),
      ...viewer.tasks(state.viewer),
      observeContractRoute(),
      observeContractAuth()
    ],
    update: (state, message): ContractSigningShellUpdate<ViewerState> => {
      const reduced = match({ state, message })
        .returnType<ContractSigningShellUpdate<ViewerState>>()
        .with({ message: { type: 'routeChanged' } }, ({ state, message }) =>
          handleContractRouteChange(state, folderIdFromRoute(message.route), signing)
        )
        .with(
          {
            state: { surface: { mode: P.union('closed', 'paymentRequired') } },
            message: { type: 'openContract' }
          },
          ({ state, message }) =>
            openSigningShell(state, { ...message, type: 'opened' }, message.document)
        )
        .with(
          {
            state: { surface: { mode: P.union('closed', 'paymentRequired') } },
            message: { type: 'prepareEngagementLetter' }
          },
          ({ state, message }) => [
            {
              ...state,
              surface: {
                mode: 'preparingEngagementLetter',
                folderId: message.folderId,
                signer: message.signer,
                openWhenReady: true
              }
            },
            prepareEngagementLetter(message.folderId)
          ]
        )
        .with(
          {
            state: { surface: { mode: 'preparingEngagementLetter', openWhenReady: true } },
            message: { type: 'engagementLetterPrepared' }
          },
          ({ state, message }) =>
            openSigningShell(
              state,
              {
                type: 'opened',
                folderId: message.folderId,
                contract: message.contract,
                target: {
                  type: 'signer',
                  signerId: match(state.surface.signer)
                    .with(
                      { type: 'first' },
                      () =>
                        contractSignerEntries(message.contract).find((entry) => entry.pending)!
                          .signer.id
                    )
                    .with(
                      { type: 'selected' },
                      ({ role }) =>
                        message.contract.signers.find((signer) => signer.role == role)!.id
                    )
                    .exhaustive()
                }
              },
              message.document
            )
        )
        .with(
          {
            state: { surface: { mode: 'preparingEngagementLetter', openWhenReady: false } },
            message: { type: 'engagementLetterPrepared' }
          },
          ({ state }) => ({ ...state, surface: { mode: 'closed' } })
        )
        .with(
          {
            state: { surface: { mode: 'signing' } },
            message: { type: 'paymentRequiredClosed' }
          },
          ({ state }) => ({
            ...state,
            surface: { ...state.surface, paymentPromptOpen: false }
          })
        )
        .with(
          {
            state: { surface: { mode: 'paymentRequired' } },
            message: { type: 'paymentRequiredClosed' }
          },
          ({ state }) => ({ ...state, surface: { mode: 'closed' } })
        )
        .with(
          {
            state: { surface: { mode: P.union('closed', 'paymentRequired') } },
            message: { type: 'signedDocumentOpened' }
          },
          ({ state, message }) => openSignedDocument(state, message)
        )
        .with(
          { state: { surface: { mode: 'signed' } }, message: { type: 'signedDocumentClosed' } },
          ({ state }) => closeShell(state)
        )
        // An accepted write owns this slot until completion; entry/dismissal
        // intents cannot replace it, even though its dialog is already hidden.
        .with(
          P.union({
            state: { surface: { mode: 'finishing' } },
            message: P.union(
              { type: 'signing', message: { type: 'closed' } },
              { type: 'openContract' },
              { type: 'prepareEngagementLetter' },
              { type: 'signedDocumentOpened' }
            )
          },
          {
            state: { surface: { mode: 'closed' } },
            message: { type: 'signing', message: { type: 'closed' } }
          }),
          ({ state }) => state
        )
        .with(
          { message: { type: 'authStatusChanged', event: { type: 'loggedOut' } } },
          ({ state }) => closeForContextLoss(state, 'sessionEnded', signing)
        )
        .with(
          {
            message: {
              type: 'authStatusChanged',
              event: { type: P.union('loggedIn', 'loginFailed') }
            }
          },
          ({ state }) => state
        )
        .with({ message: { type: 'zoomClicked' } }, ({ state, message }) => {
          const zoom = selContractZoom(state)
          const level = match(message.direction)
            .with('in', () => zoom.zoomIn)
            .with('out', () => zoom.zoomOut)
            .exhaustive()
          if (level == undefined) throw new Error('Zoom step exceeds viewer limits')
          // A chosen zoom ends the viewer's fitting to width for this document.
          return routeViewerMessage(
            state,
            { type: 'viewer', message: { type: 'setViewerZoom', level } },
            signing,
            viewer
          )
        })
        .with({ message: { type: 'viewer' } }, ({ state, message }) =>
          routeViewerMessage(state, message.message, signing, viewer, message.paymentRequired)
        )
        .with(
          {
            state: { surface: { mode: P.union('signing', 'signed') } },
            message: { type: 'bookmarkSelected' }
          },
          ({ state, message }) => navigateToPage(state, message.pageNumber)
        )
        .with(
          {
            state: { surface: { mode: P.union('signing', 'finishing') } },
            message: { type: 'signing' }
          },
          ({ state, message }) =>
            updateSigningAndSettleSurface(state, message.message, signing, message.paymentRequired)
        )
        .otherwise(({ state, message }) => {
          throw unexpectedShellMessage(state, message)
        })

      // After the signing transition, a changed cursor creates one navigation
      // request; then present, including when page geometry arrives.
      return Update.pipe(reduced, followActiveField, (state) =>
        viewer.update(
          state.viewer,
          { type: 'present', presentation: signingViewerPresentation(state) },
          (viewer) => ({ ...state, viewer })
        )
      )
    }
  })
}

function handleContractRouteChange<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  folderId: FolderId | undefined,
  signing: ContractSigningChild
): ContractSigningShellUpdate<ViewerState> {
  const activeSigning = match(state.surface)
    .with({ mode: 'signing' }, (surface) => surface.signing)
    .otherwise(() => undefined)
  if (activeSigning) {
    if (activeSigning.folderId == folderId) return state
    return closeForContextLoss(state, 'navigation', signing)
  }

  if (state.surface.mode == 'preparingEngagementLetter') {
    if (state.surface.folderId == folderId) return state
    return { ...state, surface: { ...state.surface, openWhenReady: false } }
  }

  if (state.surface.mode == 'paymentRequired') {
    if (state.surface.folderId == folderId) return state
    return { ...state, surface: { mode: 'closed' } }
  }

  if (state.surface.mode == 'signed') {
    if (state.surface.folderId == folderId) return state
    return closeShell(state)
  }

  return state
}

function openSigningShell<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  message: Extract<ContractSigningMessage, { type: 'opened' }>,
  source: STDocument
): ContractSigningShellState<ViewerState> {
  // Every independent entry starts at Summary, including a reopened document.
  // The viewer retains this request while its pages are loading.
  return navigateToPage(
    {
      ...state,
      surface: {
        mode: 'signing' as const,
        document: viewerDocument(source),
        signing: startOpeningSigningWizard(message),
        paymentPromptOpen: false
      },
      navigatedFieldId: undefined
    },
    1
  )
}

function openSignedDocument<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  message: Extract<ContractSigningShellMessage, { type: 'signedDocumentOpened' }>
): ContractSigningShellState<ViewerState> {
  return {
    ...state,
    surface: {
      mode: 'signed',
      folderId: message.folderId,
      contractId: message.contract.id!,
      document: viewerDocument(message.document)
    },
    navigation: undefined
  }
}

// Route and settle together: completion closes the surface here, never in the
// PDF projection. Commands retain the same child message address while hidden.
function updateSigningAndSettleSurface<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState> & {
    surface: Extract<ContractSurface, { mode: 'signing' | 'finishing' }>
  },
  message: ContractSigningWizardMessage,
  signing: ContractSigningChild,
  paymentRequired = false
): ContractSigningShellUpdate<ViewerState> {
  const surface = state.surface
  // Opening/review navigation are free. Start and every signature entry or
  // confirmation use the current folder policy supplied by the signing root.
  const startsSigning = match(message)
    .with(
      P.union({ type: 'startClicked' },
      { type: 'signatureCaptureOpened' },
      { type: 'signatureSelected' },
      { type: 'signatureCaptured' }),
      () => true
    )
    .otherwise(() => false)
  if (surface.mode == 'signing' && paymentRequired && startsSigning)
    return { ...state, surface: { ...surface, paymentPromptOpen: true } }
  return Update.pipe<ContractSigningShellState<ViewerState>, ContractSigningShellMessage>(
    signing.update(surface.signing, message, (next) => {
      return match(next)
        .returnType<ContractSigningShellState<ViewerState>>()
        .with({ status: 'done' }, () => ({ ...state, surface: { mode: 'closed' } }))
        .with({ status: 'paymentRequired' }, () =>
          match(surface)
            .returnType<ContractSigningShellState<ViewerState>>()
            .with({ mode: 'signing' }, ({ signing }) => ({
              ...state,
              surface: {
                mode: 'paymentRequired',
                folderId: signing.folderId,
                contractId: signing.contractId
              }
            }))
            // The client already left; the dashboard shows the requirement.
            .with({ mode: 'finishing' }, () => ({ ...state, surface: { mode: 'closed' } }))
            .exhaustive()
        )
        .with({ status: 'finishing' }, (signing) => ({
          ...state,
          surface: { mode: 'finishing', signing }
        }))
        .otherwise((signing) => {
          if (surface.mode != 'signing') throw new Error('A hidden write cannot reopen signing')
          return {
            ...state,
            surface: {
              ...surface,
              signing,
              paymentPromptOpen: surface.paymentPromptOpen && !startsSigning
            }
          }
        })
    }),
    (next) => {
      if (next.surface.mode != 'signing') return clearViewerIntent(next)
      return next
    }
  )
}

function closeForContextLoss<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  reason: 'navigation' | 'sessionEnded',
  signing: ContractSigningChild
): ContractSigningShellUpdate<ViewerState> {
  return match(state.surface)
    .with({ mode: 'preparingEngagementLetter' }, (surface) => ({
      ...state,
      surface: { ...surface, openWhenReady: false }
    }))
    .with({ mode: P.union('signing', 'finishing') }, (surface) =>
      updateSigningAndSettleSurface({ ...state, surface }, { type: 'contextLost', reason }, signing)
    )
    .with({ mode: 'paymentRequired' }, () => ({ ...state, surface: { mode: 'closed' as const } }))
    .with({ mode: 'signed' }, () => closeShell(state))
    .with({ mode: 'closed' }, () => state)
    .exhaustive()
}

function routeViewerMessage<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  message: STDocViewerRuntimeMessage,
  signing: ContractSigningChild,
  viewer: ContractViewerChild<ViewerState>,
  paymentRequired = false
): ContractSigningShellUpdate<ViewerState> {
  const surface = state.surface
  if (surface.mode != 'signing') return routeViewer(state, message, viewer)
  const selected = selectedSignature(surface.signing, message)
  if (selected) {
    return updateSigningAndSettleSurface(
      { ...state, surface },
      { type: 'signatureSelected', fieldId: selected.id },
      signing,
      paymentRequired
    )
  }
  return routeViewer(state, message, viewer)
}

function routeViewer<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  message: STDocViewerRuntimeMessage,
  viewer: ContractViewerChild<ViewerState>
): ContractSigningShellUpdate<ViewerState> {
  return viewer.update(state.viewer, message, (viewer) => ({ ...state, viewer }))
}

function selectedSignature(
  signing: ActiveContractSigningState,
  message: STDocViewerRuntimeMessage
) {
  if (signing.status != 'signing') return undefined
  if (message.type != 'viewer' || message.message.type != 'plugin') return undefined
  if (message.message.pluginId != ANNOTATIONS_PLUGIN_ID) return undefined
  const annotationMessage = message.message.message
  if (annotationMessage.type != 'selectAnnotation') return undefined
  return signing.fields.find(
    (field) => field.id == annotationMessage.annotationId && signatureEditable(signing, field)
  )
}

function closeShell<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>
): ContractSigningShellState<ViewerState> {
  return clearViewerIntent({ ...state, surface: { mode: 'closed' } })
}

// The projection presents no document once the surface leaves the PDF; the
// intents that belonged to that document go with it.
function clearViewerIntent<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>
): ContractSigningShellState<ViewerState> {
  return { ...state, navigatedFieldId: undefined, navigation: undefined }
}

function unexpectedShellMessage<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  message: ContractSigningShellMessage
) {
  return new Error(
    `Unexpected contract signing shell message ${message.type} while surface is ${state.surface.mode}`
  )
}

// A newly active signing field becomes the scroll intent once; returning to
// the same field after the cursor leaves it scrolls again.
function followActiveField<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>
): ContractSigningShellState<ViewerState> {
  if (state.surface.mode != 'signing') return state
  const activeField = match(state.surface.signing)
    .with({ status: 'signing' }, activeSignatureField)
    .otherwise(() => undefined)
  if (!activeField) {
    if (state.surface.signing.status == 'opening') return state
    return { ...state, navigatedFieldId: undefined }
  }
  if (state.navigatedFieldId == activeField.id) return state
  const navigationSeq = state.navigationSeq + 1
  return {
    ...state,
    navigationSeq,
    navigatedFieldId: activeField.id,
    navigation: {
      token: navigationSeq,
      location: { pageNumber: activeField.page, pageRegion: activeField.rect },
      selection: { annotationId: activeField.id },
      behavior: 'smooth'
    }
  }
}

// This is a pure projection: signing owns the active field and navigation
// intent; the viewer owns when the resulting presentation takes effect.
function signingViewerPresentation<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>
): ViewerPresentation<ContractViewerDocument> {
  const surface = state.surface
  if (surface.mode != 'signing' && surface.mode != 'signed') {
    return { document: null, annotations: {}, navigation: null, initialZoom: undefined }
  }

  const annotations = match(surface)
    .with({ mode: 'signing', signing: { status: P.not('opening') } }, ({ signing }) =>
      contractFieldAnnotations(signing)
    )
    .otherwise(() => ({}))
  return {
    document: surface.document,
    annotations,
    navigation: state.navigation ?? null,
    initialZoom: undefined
  }
}

function navigateToPage<ViewerState extends ContractViewerState>(
  state: ContractSigningShellState<ViewerState>,
  pageNumber: number
): ContractSigningShellState<ViewerState> {
  const navigationSeq = state.navigationSeq + 1
  return {
    ...state,
    navigationSeq,
    navigation: {
      token: navigationSeq,
      location: { pageNumber },
      selection: 'keep',
      behavior: 'smooth'
    }
  }
}

function viewerDocument(document: STDocument): ContractViewerDocument {
  if (!document.urls.view) throw new Error('Signed document has no view URL')
  return {
    sessionKey: document.urls.view,
    documentId: document.id,
    source: { src: document.urls.view },
    name: document.name,
    downloadUrl: document.urls.download,
    pageRotations: document.pageRotations,
    deletedPageNumbers: document.deletedPageNumbers ?? [],
    pageGroups: titledPageGroups(document.pageGroups)
  }
}

function contractFieldAnnotations(
  state: ActiveContractSigningState
): Record<string, ViewerFieldAnnotation> {
  const signerId = displayedContractSignerId(state)
  const values = fieldValues(state.changeset)
  return Object.fromEntries(
    state.fields.map((field) => {
      const muted = signerId != undefined && field.signerId != signerId
      const label = signingFieldLabel(state.signers, field, muted)
      const annotation = viewerFieldAnnotation({
        id: field.id,
        label,
        field,
        mode: 'signing',
        canSign: signatureEditable(state, field),
        pageNumber: field.page,
        bounds: field.rect,
        value: displayFieldValue(field, values[field.id]),
        muted
      })
      return [annotation.id, annotation]
    })
  )
}

function displayFieldValue(field: ContractField, value: string | undefined) {
  if (!value) return ''
  if (field.type != 'date') return value
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC' }).format(new Date(value))
}

// Draft creation and publication are one operation, not two user decisions.
function prepareEngagementLetter(folderId: FolderId) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const draft = yield* sdk.send({ type: 'folders/createEngagementLetterDraft', folderId })
    const contract = yield* sdk.send({
      type: 'folders/publishContract',
      folderId,
      contractId: draft.id!
    })
    if (contract.status != 'published') {
      return yield* Effect.die(
        new Error('Published engagement letter was not returned by the folder')
      )
    }
    const folder = yield* sdk.send({ type: 'folders/getOpenFolderState', folderId })
    const document = folder.documents.find((doc) => doc.id == contract.publishedDocumentId)!
    return {
      type: 'engagementLetterPrepared',
      folderId,
      contract: { ...contract, status: 'published' },
      document
    } as const
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
}

// --- Dashboard card projection ---------------------------------------------

export type ContractCardSource =
  | { status: 'unprepared'; kind: 'engagement_letter' }
  | {
      status: 'draft'
      kind: 'engagement_letter'
      contract: Contract & { status: 'draft'; kind: 'engagement_letter' }
    }
  | {
      status: 'published'
      kind: ContractKind
      contract: Contract & { status: 'published' }
      document: STDocument
    }
  | {
      status: 'submitted'
      kind: ContractKind
      contract: Contract & { status: 'submitted' }
      document: STDocument
    }

export function contractCardSource(
  contract: Contract | undefined,
  documents: STDocument[]
): ContractCardSource {
  return match(contract)
    .returnType<ContractCardSource>()
    .with(P.nullish, () => ({ status: 'unprepared', kind: 'engagement_letter' }))
    .with({ status: 'draft', kind: 'engagement_letter' }, (contract) => ({
      status: 'draft',
      kind: 'engagement_letter',
      contract
    }))
    .with({ status: 'published' }, (contract) => ({
      status: 'published',
      kind: contractKind(contract.kind),
      contract,
      document: documents.find((document) => document.id == contractDocumentId(contract))!
    }))
    .with({ status: 'submitted' }, (contract) => ({
      status: 'submitted',
      kind: contractKind(contract.kind),
      contract,
      document: documents.find((document) => document.id == contractDocumentId(contract))!
    }))
    .otherwise(() => {
      throw new Error('Contract is not available for client signing')
    })
}

export type ContractCardAction =
  | {
      type: 'view'
      enabled: boolean
      message: Extract<ContractSigningShellMessage, { type: 'signedDocumentOpened' }>
    }
  | {
      type: 'open'
      label: string
      enabled: boolean
      message: Extract<
        ContractSigningShellMessage,
        { type: 'openContract' | 'prepareEngagementLetter' }
      >
    }

export type ContractCardModel = {
  title: string
  completed: boolean
  // The heading control. Absent while several signers each act from their row.
  heading: ContractCardAction | undefined
  // One row per signer for multi-signer contracts; empty for a single signer.
  signerRows: Array<{
    signer: Signer
    roleTitle: string
    action: Extract<ContractCardAction, { type: 'open' }> | { type: 'signed' }
  }>
  // The dialog this card hosts. A folder exposes one current contract per kind.
  dialog:
    | {
        type: 'signing'
        document: ContractViewerDocument
        signing: ActiveContractSigningState
        paymentPromptOpen: boolean
      }
    | { type: 'paymentRequired' }
    | { type: 'signed'; document: ContractViewerDocument }
    | undefined
}

const contractCardTitle: Record<ContractKind, string> = {
  engagement_letter: 'Sign Engagement Letter',
  tax_return_delivery: 'Review & Sign Tax Return'
}

/**
 * Everything the dashboard card shows for one contract. Any accepted write
 * (finishing a session, preparing a letter) disables every card's actions,
 * since the shell has one surface; the archived folder disables opening only.
 */
export function selContractCard(
  state: Pick<ContractSigningShellState<ContractViewerState>, 'surface'>,
  input: {
    folderId: FolderId
    source: ContractCardSource
    isArchived: boolean
  }
): ContractCardModel {
  const { surface } = state
  const { folderId, source, isArchived } = input
  const { kind } = source
  const completed = source.status == 'submitted'
  const entries = match(source)
    .with({ status: 'unprepared' }, () => [])
    .otherwise(({ contract }) => contractSignerEntries(contract))
  const nextSigner = entries.find((entry) => entry.pending)
  const busy = surface.mode == 'finishing' || surface.mode == 'preparingEngagementLetter'
  const preparing =
    kind == 'engagement_letter' &&
    surface.mode == 'preparingEngagementLetter' &&
    surface.folderId == folderId
  const openEnabled = !busy && !isArchived
  const reviewLabel = match(preparing)
    .with(true, () => 'Preparing…')
    .with(false, () => 'Review & Sign')
    .exhaustive()
  const action = (
    message: Extract<ContractCardAction, { type: 'open' }>['message'],
    label: string
  ): Extract<ContractCardAction, { type: 'open' }> => ({
    type: 'open',
    label,
    message,
    enabled: openEnabled
  })
  const signAs = (
    source: Extract<ContractCardSource, { status: 'draft' | 'published' }>,
    signer: Signer
  ) =>
    match(source)
      .with({ status: 'draft' }, () =>
        action(
          {
            type: 'prepareEngagementLetter',
            folderId,
            signer: { type: 'selected', role: signer.role }
          },
          reviewLabel
        )
      )
      .with({ status: 'published' }, ({ contract, document }) =>
        action(
          {
            type: 'openContract',
            folderId,
            contract,
            document,
            target: { type: 'signer', signerId: signer.id }
          },
          reviewLabel
        )
      )
      .exhaustive()

  const heading = match({ source, multiSigner: entries.length > 1, nextSigner })
    .returnType<ContractCardAction | undefined>()
    .with({ source: { status: 'submitted' } }, ({ source: { contract, document } }) => ({
      type: 'view',
      enabled: !busy,
      message: { type: 'signedDocumentOpened', folderId, contract, document }
    }))
    .with({ source: { status: 'unprepared' } }, () =>
      action(
        {
          type: 'prepareEngagementLetter',
          folderId,
          signer: { type: 'first' }
        },
        reviewLabel
      )
    )
    .with({ multiSigner: true, nextSigner: P.nonNullable }, () => undefined)
    .with({ source: { status: 'published' } }, ({ source }) =>
      match(nextSigner)
        .with(P.nullish, () =>
          action(
            {
              type: 'openContract',
              folderId,
              contract: source.contract,
              document: source.document,
              target: { type: 'finish' }
            },
            'Sign'
          )
        )
        .otherwise((entry) => signAs(source, entry.signer))
    )
    .with({ source: { status: 'draft' }, nextSigner: P.nonNullable }, ({ source, nextSigner }) =>
      signAs(source, nextSigner.signer)
    )
    .otherwise(() => {
      throw new Error('Draft has no signer to prepare')
    })

  const signerRows = match(entries.length > 1)
    .returnType<ContractCardModel['signerRows']>()
    .with(false, () => [])
    .with(true, () =>
      entries.map(({ signer, pending }) => ({
        signer,
        roleTitle: signerRoleTitle(signer.role),
        action: match({ pending, source })
          .returnType<ContractCardModel['signerRows'][number]['action']>()
          .with(
            { pending: true, source: { status: P.union('draft', 'published') } },
            ({ source }) => signAs(source, signer)
          )
          .otherwise(() => ({ type: 'signed' }))
      }))
    )
    .exhaustive()

  const dialog = match({ surface, source })
    .returnType<ContractCardModel['dialog']>()
    .with(
      { surface: { mode: 'signing', signing: { folderId, kind } } },
      ({ surface: { document, signing, paymentPromptOpen } }) => ({
        type: 'signing',
        document,
        signing,
        paymentPromptOpen
      })
    )
    .with(
      {
        surface: { mode: 'paymentRequired', folderId },
        source: { status: 'published' }
      },
      ({ surface, source }) => {
        if (surface.contractId != source.contract.id) return undefined
        return { type: 'paymentRequired' }
      }
    )
    .with(
      {
        surface: { mode: 'signed', folderId },
        source: { status: 'submitted' }
      },
      ({ surface, source }) => {
        if (surface.contractId != source.contract.id) return undefined
        return { type: 'signed', document: surface.document }
      }
    )
    .otherwise(() => undefined)

  return { title: contractCardTitle[kind], completed, heading, signerRows, dialog }
}

/**
 * Zoom steps around the actual scale, since a fitted zoom falls between steps.
 * No steps until the layout is known: a zoom chosen before the first fit
 * would be relative to a page size nobody has seen.
 */
export function selContractZoom(state: { viewer: ContractViewerState }) {
  const fit = selViewerFitToWidthZoom(state.viewer.viewer, state.viewer.pages[0]?.bounds.width)
  if (fit == undefined) return { zoomOut: undefined, zoomIn: undefined }
  const level = selViewerZoomLevel(state.viewer.viewer)
  return {
    zoomOut: ST_DOC_VIEWER_ZOOM_LEVELS.findLast((step) => step < level),
    zoomIn: ST_DOC_VIEWER_ZOOM_LEVELS.find((step) => step > level)
  }
}
